import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getPendingPayment, clearPendingPayment } from "@/lib/pendingPayment";
import { isPreviewMode } from "@/lib/previewMode";
import { sanitizeReturnTo } from "@/lib/returnTo";

/**
 * Gates routes behind a successful payment.
 * - Unauthenticated → /auth
 * - Authenticated but no successful payment → /payment
 *   (unless a recent checkout is still awaiting webhook confirmation —
 *    in that case we send the user to /payment-success to keep waiting
 *    rather than bouncing them back to the paywall)
 * - Authenticated + paid (or admin) → children
 *
 * Two failure modes this guard must never fall into, because both look like a
 * broken app to a paying learner:
 *
 *  1. Deciding "unpaid" from a query that never actually reached the database.
 *     The Supabase client needs a hydrated session before RLS will return the
 *     user's own rows; querying too early returns an empty set that is
 *     indistinguishable from "this person has not paid". We wait for the
 *     session, and we treat a query *error* as undecided (retry) rather than as
 *     a negative answer.
 *  2. Losing where the learner was going. A deep link into a paid page is
 *     carried through the paywall as `?next=` so they land on the page they
 *     asked for once access is confirmed.
 */
const MAX_ACCESS_ATTEMPTS = 3;

const PaidRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [hasPending, setHasPending] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!user?.id) {
        if (!cancelled) setChecking(false);
        return;
      }

      // The session must be on the client before RLS-scoped reads can succeed.
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!sessionData?.session) {
        // Auth is still hydrating — stay in the loading state and retry.
        if (attempt < MAX_ACCESS_ATTEMPTS) {
          setTimeout(() => {
            if (!cancelled) setAttempt((a) => a + 1);
          }, 350);
          return;
        }
        if (!cancelled) setChecking(false);
        return;
      }

      // Admins bypass payment.
      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      if (cancelled) return;
      if (roles?.some((r) => r.role === "admin")) {
        setHasAccess(true);
        setChecking(false);
        return;
      }

      const { data: pays, error: paysError } = await supabase
        .from("payments")
        .select("id")
        .eq("user_id", user.id)
        .eq("payment_status", "success")
        .limit(1);
      if (cancelled) return;

      // A failed read is not a "no". Retry a couple of times before we would
      // ever bounce someone who has in fact paid.
      if ((rolesError || paysError) && attempt < MAX_ACCESS_ATTEMPTS) {
        setTimeout(() => {
          if (!cancelled) setAttempt((a) => a + 1);
        }, 400);
        return;
      }

      let paid = (pays?.length ?? 0) > 0;

      // A 7-day pass earned with reward points opens paid pages too.
      if (!paid) {
        const { data: pass } = await supabase.rpc("has_active_reward_pass", {
          _user: user.id,
          _items: ["pro_pass_7d"],
        });
        if (cancelled) return;
        paid = pass === true;
      }

      if (paid) {
        // Confirmed — clear any stale pending marker.
        clearPendingPayment();
      }

      // Pending grace window: user just paid but webhook hasn't fired yet.
      const pending = !paid ? !!getPendingPayment() : false;

      // Unpaid learners do the profile/goal setup step before the paywall.
      let onboardingNeeded = false;
      if (!paid && !pending) {
        const { data: prof } = await (supabase as any)
          .from("profiles")
          .select("onboarding_completed_at")
          .eq("id", user.id)
          .maybeSingle();
        onboardingNeeded = !prof?.onboarding_completed_at;
      }

      if (!cancelled) {
        setHasAccess(paid);
        setHasPending(pending);
        setNeedsOnboarding(onboardingNeeded);
        setChecking(false);
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [user, location.pathname, attempt]);

  // Dev-only preview door (?preview=1). Inert in production builds.
  if (!user && isPreviewMode()) return <>{children}</>;

  if (authLoading || checking) {
    return (
      <div className="min-h-dvh bg-background flex items-center justify-center px-6">
        <div className="flex flex-col items-center gap-4 text-center" role="status" aria-live="polite">
          <div
            className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
          <p className="text-sm text-muted-foreground">
            {attempt > 0 ? "Still checking your access — one moment." : "Checking your access…"}
          </p>
        </div>
      </div>
    );
  }

  // Carry the intended destination through auth and the paywall.
  const intended = sanitizeReturnTo(location.pathname + location.search);
  const nextParam = `?next=${encodeURIComponent(intended)}`;

  if (!user) {
    return (
      <Navigate
        to={`/auth${nextParam}`}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  if (!hasAccess) {
    if (hasPending && location.pathname !== "/payment-success") {
      return <Navigate to="/payment-success" replace />;
    }
    if (!hasPending) {
      return (
        <Navigate
          to={`${needsOnboarding ? "/onboarding" : "/payment"}${nextParam}`}
          replace
        />
      );
    }
  }

  return <>{children}</>;
};

export default PaidRoute;
